import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Search, Briefcase, ArrowRight, MapPin, Clock } from "lucide-react";

const ALL_CATS = ["All","Plumbing","Electrical","Roofing","Landscaping","Renovation","HVAC","Painting","Flooring","Cleaners","Photography","Private Home Chef","Child Care"];

export default function Projects() {
  const [params, setParams] = useSearchParams();
  const [cat, setCat] = useState(params.get("category") || "All");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/projects`, { params: { category: cat, q } });
      setItems(data);
    } finally { setLoading(false); }
  }, [cat, q]);

  useEffect(() => { fetchData(); }, [cat, fetchData]);
  useEffect(() => { const id = setTimeout(fetchData, 300); return () => clearTimeout(id); }, [q, fetchData]);

  return (
    <div className="max-w-7xl mx-auto px-5 lg:px-10 py-12">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
        <div>
          <div className="overline text-[#B85C47] mb-3">Project Board</div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }}>Open homeowner projects</h1>
        </div>
        <Link to="/projects/new">
          <Button data-testid="post-project-btn" className="bg-[#B85C47] hover:bg-[#a04e3b] text-white h-11 px-6">Post a project <ArrowRight className="w-4 h-4 ml-2" /></Button>
        </Link>
      </div>

      <div className="flex gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <Input data-testid="project-search-input" placeholder="Search projects…" value={q} onChange={e => setQ(e.target.value)} className="pl-10 h-12 bg-white" />
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-10">
        {ALL_CATS.map(c => (
          <button
            key={c}
            data-testid={`project-cat-${c.toLowerCase()}`}
            onClick={() => { setCat(c); setParams(c === "All" ? {} : { category: c }); }}
            className={`px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wider transition-colors ${
              cat === c ? "bg-[#314A3E] text-[#EAB89F]" : "bg-[#EAE5D9] text-[#314A3E] hover:bg-[#dcd5c2]"
            }`}
          >{c}</button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-20 text-stone-500">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-center py-20 bg-white border border-[#E2DCD0] rounded-xl">
          <Briefcase className="w-10 h-10 mx-auto text-stone-300 mb-4" />
          <div className="text-stone-600">No open projects yet.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {items.map((p, i) => (
            <Link
              key={p.project_id}
              to={`/projects/${p.project_id}`}
              data-testid={`project-card-${p.project_id}`}
              className="bg-white border border-[#E2DCD0] rounded-xl p-6 hover:-translate-y-1 hover:shadow-md transition-all fade-up"
              style={{ animationDelay: `${i * 40}ms` }}
            >
              <div className="flex items-center justify-between mb-3">
                <span className="px-2.5 py-1 bg-[#EAE5D9] rounded-full text-[10px] uppercase tracking-[0.15em] font-bold text-[#314A3E]">{p.category}</span>
                <span className="text-xs text-stone-400">{p.bid_count} bid{p.bid_count !== 1 && "s"}</span>
              </div>
              <h3 className="text-lg font-semibold tracking-tight mb-1" style={{ fontFamily: "Outfit" }}>{p.title}</h3>
              <p className="text-sm text-stone-600 line-clamp-2 mb-4">{p.description}</p>
              <div className="flex flex-wrap items-center gap-4 text-xs text-stone-500">
                <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {p.location}</span>
                {p.budget && <span className="font-semibold text-[#314A3E]">${p.budget} budget</span>}
                {p.timeline && <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {p.timeline}</span>}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
